/**
 * Authentication Provider for HMH Lunch Ordering System
 *
 * Wraps the application with MSAL provider and handles authentication state.
 */

import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import {
  MsalProvider,
  useMsal,
  useIsAuthenticated,
} from '@azure/msal-react';
import {
  PublicClientApplication,
  InteractionStatus,
} from '@azure/msal-browser';
import { useQueryClient } from '@tanstack/react-query';
import { msalConfig, loginRequest, apiConfig } from '../../config';

// Initialize MSAL instance
export const msalInstance = new PublicClientApplication(msalConfig);

// =============================================================================
// Types
// =============================================================================

export interface User {
  id: number;
  email: string | null;
  fullName: string;
  employeeNumber: string | null;
  isEmployee: boolean;
  isAdmin: boolean;
  isKitchenStaff?: boolean;
  canOrderForOthers?: boolean;
  canViewDeductions?: boolean;
  orderingBlocked?: boolean;
}

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isSsoUser: boolean;
  accessToken: string | null;
  login: () => Promise<void>;
  loginAsGuest: (name: string, employeeNumber: string) => Promise<{ success: boolean; message?: string }>;
  loginWithTicket: (ticketCode: string, guestName: string) => Promise<{ success: boolean; message?: string }>;
  loginAsKitchenStaff: (pin: string) => Promise<{ success: boolean; message?: string }>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | null>(null);

/**
 * Keep the backend refresh token, or clear it when the response has none.
 * Guest sessions get no refresh token (HMH-2418); storing `undefined` would
 * leave the string "undefined" behind and send it to /auth/refresh.
 */
function storeRefreshToken(token: string | null | undefined): void {
  if (token) {
    sessionStorage.setItem('hmh_refresh_token', token);
  } else {
    sessionStorage.removeItem('hmh_refresh_token');
  }
}

// =============================================================================
// Auth Provider Inner Component
// =============================================================================

function AuthProviderInner({ children }: { children: React.ReactNode }) {
  const { instance, accounts, inProgress } = useMsal();
  const isMsalAuthenticated = useIsAuthenticated();
  const queryClient = useQueryClient();

  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSsoUser, setIsSsoUser] = useState(false);
  const [msalInitialized, setMsalInitialized] = useState(false);
  const [ssoAttempted, setSsoAttempted] = useState(false);
  const [ssoInProgress, setSsoInProgress] = useState(false);

  // Use ref to track session restore to avoid race conditions
  const sessionRestoreAttempted = useRef(false);

  // Once loading settles, clear the SSO round-trip marker so future visits
  // in this tab start with the normal "checking" message
  useEffect(() => {
    if (!isLoading) {
      sessionStorage.removeItem('hmh_sso_redirect');
    }
  }, [isLoading]);

  /**
   * Initialize MSAL and handle redirect promise
   *
   * This handles the redirect response from Microsoft when returning from authentication.
   * It must be called before any other MSAL operations.
   */
  useEffect(() => {
    const initializeMsal = async () => {
      try {
        await instance.initialize();

        // Handle redirect response (if returning from Microsoft login)
        const redirectResponse = await instance.handleRedirectPromise();

        if (redirectResponse) {
          // User just returned from Microsoft login redirect
          console.log('[MSAL] Redirect response received:', redirectResponse.account.username);
          instance.setActiveAccount(redirectResponse.account);

          // Validate with backend inline (to avoid dependency issues)
          try {
            const response = await fetch(`${apiConfig.baseUrl}/auth/validate`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ access_token: redirectResponse.accessToken }),
            });

            if (response.ok) {
              const data = await response.json();
              setAccessToken(data.access_token);
              sessionStorage.setItem('hmh_access_token', data.access_token);
              storeRefreshToken(data.refresh_token);

              setUser({
                id: data.user.id,
                email: data.user.email,
                fullName: data.user.full_name,
                employeeNumber: data.user.employee_number,
                isEmployee: data.user.is_employee,
                isAdmin: data.user.is_admin,
        canOrderForOthers: data.user.can_order_for_others ?? false,
        canViewDeductions: data.user.can_view_deductions ?? false,
        orderingBlocked: data.user.ordering_blocked ?? false,
              });
              setIsSsoUser(true);
              console.log('[MSAL] Redirect authentication complete:', data.user.email);
            } else {
              console.error('Backend validation failed:', response.status);
            }
          } catch (error) {
            console.error('Backend validation error:', error);
          }

          setMsalInitialized(true);
          setIsLoading(false);
          return;
        }

        // No redirect response - check for existing accounts
        const accounts = instance.getAllAccounts();
        if (accounts.length > 0) {
          instance.setActiveAccount(accounts[0]);
          console.log('[MSAL] Found existing account:', accounts[0].username);
        } else {
          console.log('[MSAL] No existing accounts found');
        }

        setMsalInitialized(true);
        console.log('[MSAL] Initialization complete');
      } catch (error) {
        console.error('[MSAL] Initialization error:', error);
        setMsalInitialized(true);
        setIsLoading(false);
      }
    };

    initializeMsal();
  }, [instance]);

  /**
   * Validate token with backend and get user info
   */
  const validateWithBackend = useCallback(async (msalToken: string): Promise<User | null> => {
    try {
      const response = await fetch(`${apiConfig.baseUrl}/auth/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ access_token: msalToken }),
      });

      if (!response.ok) {
        console.error('Backend validation failed:', response.status);
        return null;
      }

      const data = await response.json();
      setAccessToken(data.access_token);
      sessionStorage.setItem('hmh_access_token', data.access_token);

      // Store refresh token
      storeRefreshToken(data.refresh_token);

      // Remember the account for future ssoSilent loginHint (survives tab close)
      if (data.user.email) {
        localStorage.setItem('hmh_last_upn', data.user.email);
      }

      return {
        id: data.user.id,
        email: data.user.email,
        fullName: data.user.full_name,
        employeeNumber: data.user.employee_number,
        isEmployee: data.user.is_employee,
        isAdmin: data.user.is_admin,
        canOrderForOthers: data.user.can_order_for_others ?? false,
        canViewDeductions: data.user.can_view_deductions ?? false,
        orderingBlocked: data.user.ordering_blocked ?? false,
      };
    } catch (error) {
      console.error('Backend validation error:', error);
      return null;
    }
  }, []);


  /**
   * Attempt to restore session from stored refresh token
   * This handles both SSO and guest login sessions
   */
  const attemptSessionRestore = useCallback(async (): Promise<boolean> => {
    const refreshToken = sessionStorage.getItem("hmh_refresh_token");
    if (!refreshToken) {
      console.log("[Auth] No stored refresh token found");
      return false;
    }

    console.log("[Auth] Attempting to restore session from refresh token...");

    try {
      const response = await fetch(`${apiConfig.baseUrl}/auth/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        console.log("[Auth] Refresh token invalid or expired");
        sessionStorage.removeItem("hmh_access_token");
        sessionStorage.removeItem("hmh_refresh_token");
        return false;
      }

      const data = await response.json();
      console.log("[Auth] Session restored successfully");

      setAccessToken(data.access_token);
      sessionStorage.setItem("hmh_access_token", data.access_token);
      storeRefreshToken(data.refresh_token);

      setUser({
        id: data.user.id,
        email: data.user.email,
        fullName: data.user.full_name,
        employeeNumber: data.user.employee_number,
        isEmployee: data.user.is_employee,
        isAdmin: data.user.is_admin,
        canOrderForOthers: data.user.can_order_for_others ?? false,
        canViewDeductions: data.user.can_view_deductions ?? false,
        orderingBlocked: data.user.ordering_blocked ?? false,
      });

      setIsSsoUser(!!data.user.email);

      return true;
    } catch (error) {
      console.error("[Auth] Session restore error:", error);
      return false;
    }
  }, []);
  /**
   * Kick off the kitchen auto-login request immediately on mount for tablet
   * routes, so the network round-trip overlaps with MSAL initialization
   * instead of waiting behind it. The response is consumed later by
   * attemptKitchenAutoLogin in the main auth flow.
   */
  const kitchenAutoLoginRequest = useRef<Promise<Response | null> | null>(null);

  const startKitchenAutoLoginRequest = (): Promise<Response | null> =>
    fetch(`${apiConfig.baseUrl}/auth/kitchen/auto-login`, {
      method: "POST",
      // Enrolled tablets carry their device secret in an HttpOnly cookie (HMH-2417)
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
    }).catch((error) => {
      console.error("[Auth] Kitchen auto-login request error:", error);
      return null;
    });

  useEffect(() => {
    const tabletRoutes = ["/kitchen", "/admin/orders", "/admin"];
    const path = window.location.pathname;
    const onTabletRoute = tabletRoutes.some((r) => path === r || path.startsWith(r + "/"));
    // A stored refresh token means session restore will win; don't bother pre-fetching
    if (onTabletRoute && !sessionStorage.getItem("hmh_refresh_token")) {
      kitchenAutoLoginRequest.current = startKitchenAutoLoginRequest();
    }
  }, []);

  /**
   * Attempt kitchen tablet auto-login with the enrolled device cookie
   * (HMH-2417; the legacy IP-only login only while an admin keeps it on).
   * Returns true if a kitchen JWT was issued. Silent on 403.
   */
  const attemptKitchenAutoLogin = useCallback(async (): Promise<boolean> => {
    try {
      // Use the pre-started request from mount if available
      const pending = kitchenAutoLoginRequest.current ?? startKitchenAutoLoginRequest();
      kitchenAutoLoginRequest.current = null;
      const response = await pending;

      if (!response || !response.ok) {
        // 403 is the expected case for non-tablet devices
        return false;
      }

      const data = await response.json();
      setAccessToken(data.access_token);
      sessionStorage.setItem("hmh_access_token", data.access_token);
      setUser({
        id: data.staff.id,
        email: null,
        fullName: data.staff.display_name,
        employeeNumber: null,
        isEmployee: false,
        isAdmin: false,
        isKitchenStaff: true,
      });
      setIsSsoUser(false);
      console.log("[Auth] Kitchen tablet auto-login granted");
      return true;
    } catch (error) {
      console.error("[Auth] Kitchen auto-login error:", error);
      return false;
    }
  }, []);

  /**
   * Proactive session renewal.
   *
   * Backend JWTs live 60 minutes; long-lived pages (kitchen displays,
   * admin tabs) previously went permanently stale after expiry — every
   * API call 401'd and the WebSocket reconnect-looped with a dead token.
   * Renew shortly before expiry, and immediately on wake if the tab was
   * suspended past expiry. All consumers read the token fresh from
   * sessionStorage, so updating it here heals REST and WebSocket alike.
   */
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refreshSession = useCallback(async (): Promise<boolean> => {
    // 1) Backend refresh token (SSO and guest sessions)
    const refreshToken = sessionStorage.getItem("hmh_refresh_token");
    if (refreshToken) {
      try {
        const resp = await fetch(`${apiConfig.baseUrl}/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
        if (resp.ok) {
          const data = await resp.json();
          setAccessToken(data.access_token);
          sessionStorage.setItem("hmh_access_token", data.access_token);
          storeRefreshToken(data.refresh_token);
          console.log("[Auth] Session renewed via refresh token");
          return true;
        }
      } catch (error) {
        console.error("[Auth] Refresh token renewal error:", error);
      }
    }

    // 2) Kitchen tablets have no refresh token: repeat the IP auto-login
    if (user?.isKitchenStaff) {
      const renewed = await attemptKitchenAutoLogin();
      if (renewed) {
        console.log("[Auth] Session renewed via kitchen auto-login");
        return true;
      }
    }

    // 3) SSO fallback: silent MSAL token + backend validation
    if (isMsalAuthenticated && accounts.length > 0) {
      try {
        const response = await instance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        });
        const userData = await validateWithBackend(response.accessToken);
        if (userData) {
          setUser(userData);
          console.log("[Auth] Session renewed via MSAL silent token");
          return true;
        }
      } catch (error) {
        console.error("[Auth] MSAL silent renewal error:", error);
      }
    }

    console.log("[Auth] Session renewal failed; signing out");
    setUser(null);
    setAccessToken(null);
    sessionStorage.removeItem("hmh_access_token");
    sessionStorage.removeItem("hmh_refresh_token");
    return false;
  }, [user, isMsalAuthenticated, accounts, instance, validateWithBackend, attemptKitchenAutoLogin]);

  useEffect(() => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    if (!accessToken) return;

    let expMs: number | null = null;
    try {
      expMs = JSON.parse(atob(accessToken.split(".")[1])).exp * 1000;
    } catch {
      return; // not a JWT; nothing to schedule
    }

    // Renew 2 minutes before expiry (never sooner than 15s from now)
    const delay = Math.max(expMs - Date.now() - 2 * 60 * 1000, 15_000);
    refreshTimer.current = setTimeout(() => {
      refreshSession();
    }, delay);

    // Suspended tabs (back-forward cache, tablet lock screens) can sleep
    // through the timer; renew immediately on wake if expiry passed
    const onWake = () => {
      if (document.visibilityState === "visible" && expMs !== null && Date.now() > expMs - 60_000) {
        refreshSession();
      }
    };
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("pageshow", onWake);

    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("pageshow", onWake);
    };
  }, [accessToken, refreshSession]);

  /**
   * Attempt silent SSO authentication.
   *
   * FIRST tries ssoSilent (hidden iframe) — authenticates without any page
   * reload, so the user sees one continuous loading state. Only if that
   * fails (e.g. third-party cookies blocked) does it fall back to the
   * full-page redirect with prompt=none.
   */
  const attemptSilentSso = useCallback(async (): Promise<void> => {
    // Only attempt once per app load
    if (ssoAttempted || sessionStorage.getItem('hmh_sso_attempted')) {
      return;
    }

    setSsoAttempted(true);
    sessionStorage.setItem('hmh_sso_attempted', 'true');
    setSsoInProgress(true);

    // FIRST: hidden-iframe silent SSO — no page reload
    try {
      console.log('[MSAL] Attempting silent SSO in hidden iframe (ssoSilent)...');
      const lastUpn = localStorage.getItem('hmh_last_upn') || undefined;
      const result = await instance.ssoSilent({
        ...loginRequest,
        loginHint: lastUpn,
        // Blank page as iframe redirect target: the SPA router would strip
        // the auth hash and cause hash_empty_error. /blank.html is already
        // a registered SPA redirect URI on the app registration.
        redirectUri: `${window.location.origin}/blank.html`,
      });
      instance.setActiveAccount(result.account);

      const userData = await validateWithBackend(result.accessToken);
      if (userData) {
        setUser(userData);
        setIsSsoUser(true);
        queryClient.invalidateQueries();
        console.log('[MSAL] Silent SSO complete without redirect:', result.account?.username);
      } else {
        console.error('[MSAL] ssoSilent succeeded but backend validation failed');
      }
      setSsoInProgress(false);
      return;
    } catch (iframeError: any) {
      const code = iframeError?.errorCode || iframeError?.name;
      console.log('[MSAL] ssoSilent unavailable, falling back to redirect:', code);
    }

    // FALLBACK: redirect with prompt=none (full page round-trip)
    try {
      console.log('[MSAL] Attempting silent SSO with redirect (prompt=none)...');

      // Mark the round-trip so the loading screen reads as one continuous
      // "signing in" step instead of appearing to start over after redirect
      sessionStorage.setItem('hmh_sso_redirect', 'true');

      await instance.loginRedirect({
        ...loginRequest,
        prompt: 'none',
      });

      // Note: execution will not continue here as the page will redirect
      // The result will be handled by handleRedirectPromise() on return
    } catch (error: any) {
      sessionStorage.removeItem('hmh_sso_redirect');

      // Handle expected error codes gracefully
      const errorCode = error?.errorCode || error?.name;
      console.log('[MSAL] Silent SSO redirect failed:', errorCode);

      // These are expected errors when SSO isn't available
      const expectedErrors = [
        'interaction_required',
        'login_required',
        'consent_required',
        'no_account_error',
      ];

      if (!expectedErrors.includes(errorCode)) {
        // Only log unexpected errors
        console.error('[MSAL] Unexpected silent SSO error:', error);
      }

      setSsoInProgress(false);
    }
  }, [instance, ssoAttempted, validateWithBackend, queryClient]);

  /**
   * Main authentication flow after MSAL is initialized
   * Priority: 1) Restore existing session, 2) MSAL cached account, 3) Silent SSO redirect
   */
  useEffect(() => {
    if (!msalInitialized) {
      return;
    }

    const handleAuth = async () => {
      if (inProgress !== InteractionStatus.None) {
        return;
      }

      if (sessionRestoreAttempted.current) {
        return;
      }
      sessionRestoreAttempted.current = true;

      // FIRST: Try to restore existing session from stored tokens
      const sessionRestored = await attemptSessionRestore();
      if (sessionRestored) {
        console.log("[Auth] User session restored from stored tokens");
        setIsLoading(false);
        return;
      }

      // KITCHEN TABLET: try enrolled-device auto-login on tablet routes only
      const kitchenTabletRoutes = ["/kitchen", "/admin/orders", "/admin"];
      const tabletPath = window.location.pathname;
      if (kitchenTabletRoutes.some((route) => tabletPath === route || tabletPath.startsWith(route + "/"))) {
        const tabletAuth = await attemptKitchenAutoLogin();
        if (tabletAuth) {
          setIsLoading(false);
          return;
        }
      }

      // SECOND: If MSAL has a cached account, use it
      if (isMsalAuthenticated && accounts.length > 0) {
        console.log("[Auth] Found MSAL cached account, validating...");
        try {
          const response = await instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
          });

          const userData = await validateWithBackend(response.accessToken);
          if (userData) {
            setUser(userData);
            setIsSsoUser(true);
            // Invalidate all queries to ensure they use the new token
            queryClient.invalidateQueries();
            setIsLoading(false);
            return;
          }
        } catch (error) {
          console.error("Silent token acquisition failed:", error);
        }
      }

      // THIRD: Try silent SSO with redirect (prompt=none)
      // Skip SSO for public routes that don't require authentication
      const publicRoutes = ['/kitchen', '/kiosk', '/login'];
      const currentPath = window.location.pathname;

      if (publicRoutes.some(route => currentPath.startsWith(route))) {
        console.log('[Auth] On public route, skipping SSO attempt:', currentPath);
        setIsLoading(false);
        return;
      }

      // This will redirect to Microsoft and back automatically
      // If successful, handleRedirectPromise will process the response
      // If failed, user will see the login page
      await attemptSilentSso();

      // If we reach here without redirect, show login page
      setIsLoading(false);
    };

    handleAuth();
  }, [msalInitialized, isMsalAuthenticated, accounts, inProgress, instance, validateWithBackend, attemptSilentSso, attemptSessionRestore, attemptKitchenAutoLogin, queryClient]);

  /**
   * Login with popup
   *
   * Uses popup flow with explicit configuration to prevent hash_empty_error.
   */
  const login = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log('[MSAL] Starting login popup with request:', loginRequest);

      // Open popup with explicit configuration
      const response = await instance.loginPopup({
        ...loginRequest,
        // Explicitly set redirect URI for popup
        redirectUri: `${window.location.origin}/blank.html`,
      });

      console.log('[MSAL] Login popup successful:', response.account.username);
      instance.setActiveAccount(response.account);

      // Validate with backend
      console.log('[MSAL] Acquiring token silently for backend validation');
      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account: response.account!,
      });

      console.log('[MSAL] Token acquired, validating with backend');
      const userData = await validateWithBackend(tokenResponse.accessToken);
      if (userData) {
        setUser(userData);
        setIsSsoUser(true);
        console.log('[MSAL] User validated and logged in:', userData.email);
      } else {
        console.error('[MSAL] Backend validation failed');
        throw new Error('Backend validation failed');
      }
    } catch (error: any) {
      console.error('[MSAL] Login failed:', error);
      console.error('[MSAL] Error details:', {
        errorCode: error?.errorCode,
        errorMessage: error?.errorMessage,
        subError: error?.subError,
        name: error?.name,
        message: error?.message,
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [instance, validateWithBackend]);

  /**
   * Guest login (employee login with name and employee number)
   */
  const loginAsGuest = useCallback(async (
    name: string,
    employeeNumber: string
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      setIsLoading(true);
      const response = await fetch(`${apiConfig.baseUrl}/auth/login/guest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          employee_number: employeeNumber,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, message: data.detail || 'Login failed' };
      }

      // Check if user is not an employee
      if (data.is_employee === false) {
        return { success: false, message: data.message };
      }

      // Successful employee login
      setAccessToken(data.access_token);
      sessionStorage.setItem('hmh_access_token', data.access_token);
      storeRefreshToken(data.refresh_token);

      setUser({
        id: data.user.id,
        email: data.user.email,
        fullName: data.user.full_name,
        employeeNumber: data.user.employee_number,
        isEmployee: data.user.is_employee,
        isAdmin: data.user.is_admin,
        canOrderForOthers: data.user.can_order_for_others ?? false,
        canViewDeductions: data.user.can_view_deductions ?? false,
        orderingBlocked: data.user.ordering_blocked ?? false,
      });
      setIsSsoUser(false);

      return { success: true };
    } catch (error) {
      console.error('Guest login error:', error);
      return { success: false, message: 'An error occurred during login' };
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Ticket login (guest login with ticket code)
   */
  const loginWithTicket = useCallback(async (
    ticketCode: string,
    guestName: string
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      setIsLoading(true);
      const response = await fetch(`${apiConfig.baseUrl}/kiosk/tickets/redeem`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ticket_code: ticketCode,
          guest_name: guestName,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, message: data.detail || 'Ticket redemption failed' };
      }

      // Create a guest user session with the ticket information
      // Note: Ticket users don't get a JWT token, they use the ticket as auth
      setUser({
        id: 0, // Temporary ID for ticket users
        email: null,
        fullName: guestName,
        employeeNumber: null,
        isEmployee: false,
        isAdmin: false,
      });
      setIsSsoUser(false);

      // Store ticket code for order placement
      sessionStorage.setItem('hmh_ticket_code', ticketCode);
      sessionStorage.setItem('hmh_guest_name', guestName);

      return { success: true };
    } catch (error) {
      console.error('Ticket login error:', error);
      return { success: false, message: 'An error occurred during ticket validation' };
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Kitchen staff login (PIN only)
   */
  const loginAsKitchenStaff = useCallback(async (
    pin: string
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      setIsLoading(true);
      const response = await fetch(`${apiConfig.baseUrl}/auth/kitchen-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pin,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, message: data.detail || 'Login failed' };
      }

      // Successful kitchen staff login
      setAccessToken(data.access_token);
      sessionStorage.setItem('hmh_access_token', data.access_token);

      setUser({
        id: data.staff.id,
        email: null,
        fullName: data.staff.display_name,
        employeeNumber: null,
        isEmployee: false,
        isAdmin: false,
        isKitchenStaff: true,
      });
      setIsSsoUser(false);

      return { success: true };
    } catch (error) {
      console.error('Kitchen staff login error:', error);
      return { success: false, message: 'An error occurred during login' };
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Logout
   */
  const logout = useCallback(async () => {
    const wasSsoUser = isSsoUser;

    setUser(null);
    setAccessToken(null);
    setIsSsoUser(false);
    setSsoAttempted(false);
    sessionRestoreAttempted.current = false;
    sessionStorage.removeItem('hmh_access_token');
    sessionStorage.removeItem('hmh_refresh_token');
    sessionStorage.removeItem('hmh_ticket_code');
    sessionStorage.removeItem('hmh_guest_name');
    sessionStorage.removeItem('hmh_sso_attempted');

    if (wasSsoUser) {
      try {
        await instance.logoutPopup({
          postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri,
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
  }, [instance, isSsoUser]);

  /**
   * Get current access token (refresh if needed)
   */
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (accessToken) {
      return accessToken;
    }

    // Check sessionStorage for existing token
    const storedToken = sessionStorage.getItem('hmh_access_token');
    if (storedToken) {
      setAccessToken(storedToken);
      return storedToken;
    }

    // Try to refresh
    const refreshToken = sessionStorage.getItem('hmh_refresh_token');
    if (refreshToken) {
      try {
        const response = await fetch(`${apiConfig.baseUrl}/auth/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });

        if (response.ok) {
          const data = await response.json();
          setAccessToken(data.access_token);
      sessionStorage.setItem('hmh_access_token', data.access_token);
          storeRefreshToken(data.refresh_token);
          return data.access_token;
        }
      } catch (error) {
        console.error('Token refresh failed:', error);
      }
    }

    return null;
  }, [accessToken]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading: isLoading || !msalInitialized || ssoInProgress,
    isSsoUser,
    accessToken,
    login,
    loginAsGuest,
    loginWithTicket,
    loginAsKitchenStaff,
    logout,
    getAccessToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// =============================================================================
// Exports
// =============================================================================

export function AuthProvider({ children }: { children: React.ReactNode }) {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthProviderInner>{children}</AuthProviderInner>
    </MsalProvider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
